import { ArrowLeftIcon, PhotoIcon, UserCircleIcon, TicketIcon } from '@heroicons/react/20/solid'
import { t } from 'i18next'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { GET_BOAT_DAY_PASS_PRICE } from 'graphql/booking/queries'
import loadingImage from '../../../assets/images/boat-loading.gif'
import { useLazyQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { customDateFormat } from 'utils/common'

export default function DayPassDetails() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [passPriceData, setPassPriceData] = useState(null)
    const [getPassPriceList, { loading: passPriceListLoading, error: passPriceListError }] = useLazyQuery(
        GET_BOAT_DAY_PASS_PRICE,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getBoatDayPassPrices.success && data.getBoatDayPassPrices.code === '200') {
                    setPassPriceData(data.getBoatDayPassPrices.day_pass_prices[0])
                }
            },
        }
    )

    // Search boats Boat List
    const searchBoatPass = () => {
        getPassPriceList({
            variables: {
                page: {
                    limit: 1,
                    offset: 0,
                },
                priceFilter: {
                    master_price_id: id,
                    valid_from: null,
                    valid_to: null,
                    status_code: null,
                    price_status_code: null,
                },
            },
        })
    }

    useEffect(() => {
        if (id) searchBoatPass()
    }, [id])

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button onClick={() => navigate('/day-pass/price')} className="inline-flex items-center gap-x-1.5 rounded-md text-sm">
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
                {passPriceData?.is_editable && (
                    <button
                        onClick={() => navigate(`/day-pass/add-price/${id}`)}
                        className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    >
                        {t('Edit Day Pass')}
                    </button>
                )}

            </div>
            {passPriceListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait')}...</p>
                </div>
            )}
            {passPriceListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch banner!')}</div>
                </div>
            )}
            {!passPriceListLoading && !passPriceListError && passPriceData === null && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!passPriceListLoading && !passPriceListError && passPriceData !== null && (
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="max-sm:w-full sm:flex-1">
                        <div className="mt-3">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {t('Day Pass Information')}
                                </h3>
                            </div>

                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Name (English)')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {passPriceData?.name?.en || '-'}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Name (Arabic)')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0" dir="rtl">
                                            {passPriceData?.name?.ar || '-'}
                                        </dd>
                                    </div>
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Description (English)')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {passPriceData?.description?.en || '-'}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Description (Arabic)')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0" dir="rtl">
                                            {passPriceData?.description?.ar || '-'}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <div className="mt-6">
                            <div className="px-4 sm:px-0">
                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                    {' '}
                                    {t('Day Pass Validity')}
                                </h3>
                            </div>

                            <div className="mt-3 border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                    <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">
                                            {t('Valid From')}
                                        </dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {customDateFormat(passPriceData.valid_from)}
                                        </dd>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                        <dt className="text-sm font-medium leading-6 text-gray-900">{t('Valid To')}</dt>
                                        <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                            {customDateFormat(passPriceData.valid_to)}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            {passPriceData.price.map((priceInfo, index) => (
                                <div key={priceInfo.price_id} className="mt-6">
                                    <div className="px-4 sm:px-0 flex items-center">
                                        <div className="sm:col-span-full flex gap-1 items-center ms-0">
                                            <TicketIcon
                                                aria-hidden="true"
                                                className={`h-9 w-9 ${priceInfo.seat_type?.code === 'VIP' ? 'text-gold' : 'text-blue'
                                                    }`}
                                            />
                                            <div className="text-end flex justify-between w-full">
                                                <h3 className="text-lg font-semibold leading-7 text-primary">
                                                    {t(priceInfo.seat_type?.description?.en)}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-3 border-t border-gray-100">
                                        <dl className="divide-y divide-gray-100">
                                            <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    {' '}
                                                    {t('Infant')}
                                                </dt>
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    {priceInfo.price.INF} BHD
                                                </dd>
                                            </div>
                                            <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    {' '}
                                                    {t('Child')}
                                                </dt>
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    {priceInfo.price.CHD} BHD
                                                </dd>
                                            </div>
                                            <div className="bg-white px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    {' '}
                                                    {t('Adult')}
                                                </dt>
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    {priceInfo.price.ADL} BHD
                                                </dd>
                                            </div>
                                            <div className="bg-gray-50 px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-3">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">
                                                    {t('Senior Citizen')}
                                                </dt>
                                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                                    {priceInfo.price.SNR} BHD
                                                </dd>
                                            </div>
                                        </dl>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
